import {
    DialogActions,
    DialogTitle
} from "@material-ui/core";
import React, { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import {
    IWorkspacePermission,
    IWorkspaceResourceFolder,
    IWorkspaceUserGroup,
    WorkspacePermissionSubjectType,
    WorkspaceUserGroupType,
    WorkspaceUserRole
} from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import { Button } from "js/Components/Button";
import { ToggleSwitch } from "js/Components/ToggleSwitch";
import WorkspaceController, {
    WorkspaceControllerState
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import { BeautifulDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";

const logger = getLogger();

const DialogContent = styled.div`
    padding: 0 25px;
    font-size: 14px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    position: relative;
`;

const SettingContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 12px;

    >span {
        margin-left: 60px;
    }
`;

const SwitchContainer = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 10px;
    font-size: 14px;
    font-weight: 600;
`;

type AllTeamMembersFolderSettingsDialogProps = WorkspaceControllerState & {
    folderId: string;
    closeDialog: () => void;
}

function getRestrictAccess(folder: IWorkspaceResourceFolder) {
    if (folder.permissionsOverrides?.[WorkspaceUserRole.MEMBER]?.write.object) {
        return false;
    }

    return true;
}

function getHideFolder(folderUserGroup: IWorkspaceUserGroup, userGroups: IWorkspaceUserGroup[]) {
    const membersGroup = userGroups.find(group => group.type === WorkspaceUserGroupType.MEMBERS);

    if (!membersGroup.groupIds.includes(folderUserGroup.id)) {
        return true;
    }

    return false;
}

export default WorkspaceController.withInitializedState(forwardRef<any, AllTeamMembersFolderSettingsDialogProps>(
    function AllTeamMembersFolderSettingsDialog({ folderId, resourceFolders, userGroups, closeDialog }, ref) {
        const { t } = useTranslation();
        const folder = resourceFolders.find(folder => folder.id === folderId);
        if (!folder) {
            throw new Error(`Folder ${folderId} not found`);
        }

        const userGroup = userGroups.find(group => group.id === folder.userGroupId);
        if (!userGroup) {
            throw new Error(`User group ${folder.userGroupId} not found`);
        }

        const [fetching, setFetching] = useState(false);

        const [restrictAccess, setRestrictAccess] = useState(false);
        const [hideFolder, setHideFolder] = useState(false);

        useEffect(() => {
            setHideFolder(getHideFolder(userGroup, userGroups));
        }, [getObjectHash(userGroups)]);

        useEffect(() => {
            setRestrictAccess(getRestrictAccess(folder));
        }, [getObjectHash(folder.permissionsOverrides)]);

        const handleSave = async () => {
            setFetching(true);
            try {
                await Promise.all([
                    (async () => {
                        if (hideFolder !== getHideFolder(userGroup, userGroups)) {
                            const permissions: Pick<IWorkspacePermission, "subjectId" | "subjectType">[] = [];

                            const ownersGroup = userGroups.find(group => group.type === WorkspaceUserGroupType.OWNERS);
                            const librariansGroup = userGroups.find(group => group.type === WorkspaceUserGroupType.LIBRARIANS);
                            const membersGroup = userGroups.find(group => group.type === WorkspaceUserGroupType.MEMBERS);

                            permissions.push({
                                subjectId: ownersGroup.id,
                                subjectType: WorkspacePermissionSubjectType.USER_GROUP
                            });

                            if (!hideFolder) {
                                permissions.push({
                                    subjectId: librariansGroup.id,
                                    subjectType: WorkspacePermissionSubjectType.USER_GROUP
                                }, {
                                    subjectId: membersGroup.id,
                                    subjectType: WorkspacePermissionSubjectType.USER_GROUP
                                });
                            }

                            await WorkspaceController.updateWorkspaceResourceFolderUsers({
                                folderId: folder.id,
                                update: {
                                    replace: permissions
                                }
                            });
                        }
                    })(),
                    (async () => {
                        if (restrictAccess !== getRestrictAccess(folder)) {
                            await WorkspaceController.updateWorkspaceResourceFolder({
                                folderId: folder.id,
                                permissionsOverrides: {
                                    [WorkspaceUserRole.MEMBER]: {
                                        read: {
                                            object: true,
                                            permissions: true,
                                        },
                                        write: {
                                            object: !restrictAccess,
                                            permissions: false,
                                        },
                                        owner: false
                                    }
                                }
                            });
                        }
                    })()
                ]);
            } catch (err) {
                logger.error(err, `Failed to save permissions for folder ${folder.id}`);

                ShowErrorDialog({
                    error: t("Error"),
                    message: t("Failed to save settings")
                });
            } finally {
                setFetching(false);
            }
        };

        const dirty = restrictAccess !== getRestrictAccess(folder) || hideFolder !== getHideFolder(userGroup, userGroups);

        return (
            <BeautifulDialog closeDialog={closeDialog} ref={ref}>
                <DialogTitle>{t("All Team Members Folder Settings")}</DialogTitle>
                <DialogContent>
                    <FetchingClickShield visible={fetching} backgroundColor="white" />
                    <SettingContainer>
                        <SwitchContainer>
                            <ToggleSwitch
                                value={restrictAccess}
                                onChange={() => setRestrictAccess(!restrictAccess)}
                            />
                            <span>{t("Restrict Access")}</span>
                        </SwitchContainer>
                        <span>{t("Only Librarians and Owners can add and remove presentations from this folder.")}</span>
                    </SettingContainer>
                    <SettingContainer>
                        <SwitchContainer>
                            <ToggleSwitch
                                value={hideFolder}
                                onChange={() => setHideFolder(!hideFolder)}
                            />
                            <span>{t("Hide Folder")}</span>
                        </SwitchContainer>
                        <span>{t("Hide folder from all team members. Presentations currently in this folder will not be deleted, but some members may lose access.")}</span>
                    </SettingContainer>
                </DialogContent>
                <DialogActions>
                    <Button unframed disabled={fetching} onClick={closeDialog}>{t("Close")}</Button>
                    <Button blue disabled={fetching || !dirty} onClick={handleSave}>{t("Save changes")}</Button>
                </DialogActions>
            </BeautifulDialog>
        );
    })
);
